import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
	BrandCreationData,
	BrandDetailsInfo,
	BrandGDPR,
	BrandImportPreview,
	BrandList,
	Brands,
	BrandsDetails
} from '@models/brands.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AccessTokens, FileData, ServerDataResponse } from '@models/models';
import { of } from 'rxjs/observable/of';
import { AuthTokensService } from '../../../web/modern/auth-tokens.service';
import { HttpServiceProvider } from '@services/helpers/http.service';
import { MessageService } from '@services/helpers/message-service/message.service';

@Injectable()
export class BrandsDataServiceProvider {
	private brandsData$: BehaviorSubject<Brands> = new BehaviorSubject(null);
	private brandsChange$: Subject<boolean> = new Subject();

	constructor(
		private http: HttpClient,
		private tokenService: AuthTokensService,
		private httpService: HttpServiceProvider,
		private messageService: MessageService
	) {}

	get brandsData(): Observable<Brands> {
		return this.brandsData$.asObservable();
	}

	get brandsChange(): Observable<boolean> {
		return this.brandsChange$.asObservable();
	}

	setBrandsChange(change: boolean): void {
		this.brandsChange$.next(change);
	}

	setBrandsData(brands: Brands): void {
		this.brandsData$.next(brands);
	}

	// Used to process data update due to brand switching
	updateBrands(): Observable<Brands> {
		return this.getBrands();
	}

	// get initial brand data
	getBrandsData(): void {
		this.getBrands().subscribe();
	}

	getBrandsInfo(): Observable<BrandList> {
		const url = '/api/user/brands/management';

		return this.http.get(url).pipe(
			map((res: ServerDataResponse<BrandList>): BrandList => res.data),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	switchBrands(companyId: number): Observable<AccessTokens> {
		const token = this.tokenService.getRefreshToken();
		const url = '/oauth/token';
		const params: HttpParams = new HttpParams()
			.set('grant_type', 'refresh_token')
			.set('refresh_token', token)
			.set('companyId', companyId.toString());

		const headers = new HttpHeaders().set(
			'Authorization',
			this.tokenService.getCorrectBasicWebToken()
		);

		return this.http.post(url, {}, { params, headers }).pipe(
			switchMap((data: AccessTokens): Observable<AccessTokens> => {
				if (data.access_token) {
					this.tokenService.removeTokens();
					this.tokenService.emitTokenSubject(
						this.tokenService.successGetRefreshedToken(data)
					);
					this.setActivebrandId(companyId);
				}
				return of(data);
			}),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	saveBrand(brand: BrandCreationData): Observable<BrandDetailsInfo> {
		const url = '/api/user/brands/management/new';

		return this.http.post(url, brand).pipe(
			map(
				(res: ServerDataResponse<BrandDetailsInfo>): BrandDetailsInfo =>
					res.data
			),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	getBrandDetailsInfo(id: number): Observable<BrandsDetails> {
		const url = `/api/user/brands/${id}/preview`;

		return this.http.get(url).pipe(
			map((res: ServerDataResponse<BrandsDetails>): BrandsDetails => res.data),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	getBrandsImportData(): Observable<BrandImportPreview> {
		const url = '/api/user/v1/brand/import/preview';

		return this.http.get(url).pipe(
			map(
				(res: ServerDataResponse<BrandImportPreview>): BrandImportPreview =>
					res.data
			),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	deleteBrand(url: string): Observable<Object> {
		return this.http
			.post(url, {})
			.pipe(
				catchError(error => this.messageService.catchErrorFromBackend(error))
			);
	}

	saveBrandDetailsInfo(url: string, data): Observable<Object> {
		return this.http
			.post(url, data)
			.pipe(
				catchError(error => this.messageService.catchErrorFromBackend(error))
			);
	}

	changeGdprState(url: string, data): Observable<Object> {
		return this.http.post(url, data).pipe(
			map((data: ServerDataResponse<BrandGDPR>): BrandGDPR => data.data),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	updateMedia(url: string, file: FileData): Observable<Object> {
		return this.http.post(url, file);
	}

	deleteMedia(url: string): Observable<Object> {
		return this.http
			.post(url, {})
			.pipe(
				catchError(error => this.messageService.catchErrorFromBackend(error))
			);
	}

	getEditMedia(url: string): Observable<Object> {
		return this.http
			.get(url)
			.pipe(
				catchError(error => this.messageService.catchErrorFromBackend(error))
			);
	}

	getActiveBrandId(): string {
		return localStorage.getItem('activeBrand');
	}

	setActivebrandId(id: number): void {
		localStorage.setItem('activeBrand', id.toString());
	}

	private getBrands(): Observable<Brands> {
		const url = '/api/user/brands/authorization';

		return this.http.get(url).pipe(
			map((res: ServerDataResponse<Brands>): Brands => res.data),
			map((res: Brands): Brands => this.successGetBrands(res)),
			catchError(error => this.messageService.catchErrorFromBackend(error))
		);
	}

	private successGetBrands(brands: Brands): Brands {
		this.setBrandsData(brands);
		return brands;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/brands/brands.data.service.ts